import React, { useState, useEffect, useContext } from 'react';
import { Link } from "react-router-dom";
import BootstrapTable from 'react-bootstrap-table-next';
import filterFactory, { dateFilter, Comparator } from 'react-bootstrap-table2-filter';
import { Type } from 'react-bootstrap-table2-editor';
import { Slide, Container, createMuiTheme, Dialog, Toolbar, Typography, Fab, CssBaseline, Backdrop, CircularProgress, Grid, AppBar } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/styles';
import { makeStyles } from "@material-ui/core/styles";
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import 'date-fns';
import moment from 'moment';
import styles from "../../jss/material-dashboard-react/views/dashboardStyle.js";
import { GlobalTheme } from '../../layouts/theme';
import { Theme } from '../contexts/themeContext.js';
import { Context } from "../contexts/siteContext";
import { SCContext } from '../contexts/safetyCoachingContext.js';
import { HttpService } from "../../apiAuthorization/httpService";
import { OfflineService } from "../../offline/storageService/offlineService";
import { ConvertToCurrentTimeZone } from '../header/timeFormatter';
import { TransactionTime } from '../common/transactionTime';
import Card from "../card/card.js";
import CardBody from "../card/cardBody.js";
import SafetyCoachingAdd from '../safetyCoaching/safetyCoachingAdd';
import SmallMenu from '../header/smallMenu';
import StatusIcon from '../common/statusIcon';

const useStyles = makeStyles(styles);
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function SafetyCoachingList(props) {
    const classes = useStyles();
    const offlineService = new OfflineService();
    const { defaultSite } = useContext(Context);
    const { theme } = useContext(Theme);
    const { employees, getEmployees, supervisors, getSupervisors } = useContext(SCContext);
    const [safetyCoachings, setSafetyCoachings] = useState([]);
    const [safetyCoachDate, setSafetyCoachDate] = useState(ConvertToCurrentTimeZone(new Date(), "L", null, defaultSite));
    const [safetyCoachingEditOpen, setSafetyCoachingEditOpen] = useState(false);
    const [recordToBeEdit, setRecordToBeEdit] = useState([]);
    const [backDrop, setBackDrop] = useState(true);
    const currentTheme = theme ? createMuiTheme(GlobalTheme[theme]) : createMuiTheme(GlobalTheme[localStorage.getItem("theme")]);
    const SiteId = defaultSite.id;

    const rowStyle = (row, rowIndex) => {
        return { backgroundColor: rowIndex % 2 === 0 ? '' : currentTheme.alternateGridRow };
    };

    const addSafetyCoaching = () => {
        setRecordToBeEdit([]);
        setSafetyCoachingEditOpen(true);
    };

    const editSafetyCoaching = (row) => {
        setRecordToBeEdit(row);
        setSafetyCoachingEditOpen(true);
    };

    const closeSafetyCoachingEdit = () => {
        TransactionTime.cancel();
        setSafetyCoachingEditOpen(false);
    };

    const updateList = (item) => {
        if (item !== null) {
            const data = safetyCoachings;
            const index = data.findIndex(d => d.AppSafetyCoachId === item.AppSafetyCoachId);
            if (index === -1)
                data.push(item);
            else
                data[index] = item;
            setSafetyCoachings(data);
        }
    };

    const getSafetyCoachList = () => {
        HttpService.post("/SafetyCoaching/GetSafetyCoachings", { SiteId: SiteId, SafetyCoachDate: safetyCoachDate })
            .then(async (result) => {
                if (result && result.offline) {
                    //get offline data
                    const offlineResult = await offlineService.getOfflinesBySite(defaultSite.id, "SafetyCoaching");
                    let data = offlineResult.OfflineData.data.filter(x => (moment(x.SafetyCoachDate).isSame(safetyCoachDate)));
                    setSafetyCoachings(data);
                }
                else {
                    setSafetyCoachings(result.data);
                }
                setBackDrop(false);
            },
                (error) => {
                    console.log(error);
                });
    };

    useEffect(() => {
        setBackDrop(true);
        if (employees.length === 0 || employees[0].TeamCoachId !== defaultSite?.IntegratedSystem?.TeamCoachId)
            getEmployees();
        if (supervisors.length === 0 || supervisors[0].TeamCoachId !== defaultSite?.IntegratedSystem?.TeamCoachId)
            getSupervisors();
        getSafetyCoachList();
        // eslint-disable-next-line 
    }, [safetyCoachDate]);

    const fetchData = () => {
        const columns = [
            {
                dataField: 'AppSafetyCoachId',
                text: 'ID',
                hidden: true
            },
            {
                dataField: 'rowNumber',
                text: '#',
                formatter: (cellContent, row, index) => { return (<div>{index + 1}</div>); },
                headerStyle: {
                    color: currentTheme.headerColor,
                    background: currentTheme.headerBackground,
                    width: '35px'
                },
                style: { color: currentTheme.text }
            },
            {
                dataField: 'SafetyCoachDate',
                text: 'Date',
                editor: { type: Type.DATE },
                formatter: (cell) => {
                    return new Date(cell).toLocaleDateString("en-US");
                },
                filter: dateFilter({
                    defaultValue: { date: new Date() },
                    withoutEmptyComparatorOption: false,
                    comparators: [Comparator.EQ, Comparator.GT, Comparator.LT],
                    onFilter: item => {
                        setSafetyCoachDate(new Date(item.date).toLocaleDateString("en-US"));
                    },
                    dateStyle: { color: currentTheme.headerColor, backgroundColor: currentTheme.headerBackground }
                }),
                headerStyle: {
                    width: '160px',
                    color: currentTheme.headerColor,
                    background: currentTheme.headerBackground
                },
                style: (cell, row, rowIndex, colIndex) => {
                    return {
                        color: currentTheme.text
                    };
                }
            },
            {
                dataField: 'IsSafe',
                text: 'Safe/Unsafe',
                formatter: (cell) => { return cell ? "Safe" : "Unsafe" },
                headerStyle: {
                    width: '110px',
                    color: currentTheme.headerColor,
                    background: currentTheme.headerBackground
                },
                style: { color: currentTheme.text }
            },
            {
                dataField: 'RetrainingCompleted',
                text: 'Retraining Completed',
                formatter: (cell, row) => { return row.IsSafe ? "N/A" : (cell === true ? "Yes" : "No") },
                headerStyle: {
                    width: '170px',
                    color: currentTheme.headerColor,
                    background: currentTheme.headerBackground
                },
                style: { color: currentTheme.text }
            },
            {
                dataField: 'Employee',
                text: 'Employee',
                headerStyle: {
                    width: '200px',
                    color: currentTheme.headerColor,
                    background: currentTheme.headerBackground
                },
                style: { color: currentTheme.text }
            },
            {
                dataField: 'JobTitle',
                text: 'Job Title',
                headerStyle: {
                    width: '200px',
                    color: currentTheme.headerColor,
                    background: currentTheme.headerBackground
                },
                style: { color: currentTheme.text }
            },
            {
                dataField: 'Supervisor',
                text: 'Supervisor',
                headerStyle: {
                    width: '200px',
                    color: currentTheme.headerColor,
                    background: currentTheme.headerBackground
                },
                style: { color: currentTheme.text }
            },
            {
                dataField: 'Category',
                text: 'Category',
                headerStyle: {
                    width: '200px',
                    color: currentTheme.headerColor,
                    background: currentTheme.headerBackground
                },
                style: { color: currentTheme.text }
            },
            {
                dataField: 'Activities',
                text: 'Activity',
                headerStyle: {
                    width: '200px',
                    color: currentTheme.headerColor,
                    background: currentTheme.headerBackground
                },
                style: { color: currentTheme.text }
            },
            {
                dataField: 'Comment',
                text: 'Comment',
                headerStyle: {
                    width: '200px',
                    color: currentTheme.headerColor,
                    background: currentTheme.headerBackground
                },
                style: { color: currentTheme.text }
            },
            {
                dataField: 'df1',
                text: '',
                isDummyField: true,
                headerStyle: {
                    width: '50px',
                    background: currentTheme.headerBackground
                },
                formatter: (cellcontent, row) => {
                    return (
                        <EditIcon style={{ color: currentTheme.secondary }} onClick={() => { editSafetyCoaching(row); }} />
                    );
                }
            }
        ];

        return (
            <React.Fragment>
                <ThemeProvider theme={currentTheme}>
                    <CssBaseline />
                    <Backdrop className={classes.backdrop} id="safetyCoachingBackdrop" open={backDrop}>
                        <CircularProgress color="inherit" />
                    </Backdrop>
                    <Dialog zindex="100" fullScreen open={safetyCoachingEditOpen} onClose={closeSafetyCoachingEdit} TransitionComponent={Transition}>
                        <SafetyCoachingAdd parentCallback={closeSafetyCoachingEdit}
                            refreshGrid={updateList}
                            model={recordToBeEdit}
                        />
                    </Dialog>
                    <Container maxWidth="xl" style={{ height: "95%" }} className={props.styles}>
                        <AppBar style={{ background: currentTheme.primary, color: currentTheme.color }}>
                            <Toolbar>
                                <Link to="/"><KeyboardBackspaceIcon /></Link>
                                <Grid container direction="row" alignItems="center">
                                    <Grid item>
                                        <Typography variant="h6" className={classes.title}>&nbsp;Safety Coaching</Typography>
                                    </Grid>
                                    <Grid item>
                                        <StatusIcon />
                                    </Grid>
                                </Grid>
                                <Grid container justify="flex-end" spacing={2}>
                                    <Fab style={{ background: currentTheme.secondary, color: "white" }} size="small" aria-label="add" onClick={addSafetyCoaching}>
                                        <AddIcon />
                                    </Fab>
                                    <SmallMenu moduleName={"SafetyCoaching"} modified={false} />
                                </Grid>
                            </Toolbar>
                        </AppBar>
                        <Card color="primary" style={{ height: "100%", background: currentTheme.background }}>
                            <CardBody style={{ color: currentTheme.text, marginTop: "50px" }}>
                                <BootstrapTable bootstrap4 hover wrapperClasses="table-responsive" keyField="AppSafetyCoachId" noDataIndication="No records found"
                                    data={safetyCoachings} columns={columns} rowStyle={rowStyle} filter={filterFactory()} filterPosition="top" />
                            </CardBody>
                        </Card>
                    </Container>
                </ThemeProvider>
            </React.Fragment>
        );
    }

    let content = fetchData();

    return (
        <>
            {content}
        </>
    );
}
